import type { ReactElement, ReactNode } from "react";
import {
  AlertTriangle,
  BellOff,
  CalendarClock,
  CheckCircle2,
  ChevronsRight,
  Edit3,
  Eye,
  GitBranch,
  Inbox,
  Megaphone,
  Pencil,
  Plus,
  Reply,
  Route,
  StickyNote,
  ThumbsDown,
  ThumbsUp,
  Trash2,
  Users,
  Workflow as WorkflowIcon,
  type LucideIcon,
} from "lucide-react";
import { color } from "@tokens/index";
import { Text } from "@primitives/index";
import type {
  TimelineEvent,
  TimelineEventWorkItem,
} from "../../../experience-shell/workflows/timeline";
import { ChangeDiff } from "./ChangeDiff";
import { WorkItemBody } from "./WorkItemBody";
import {
  actorLabel,
  describeChange,
  indefiniteArticle,
  queueMemberCountLabel,
  queueMemberLabel,
  workItemNoun,
} from "./payload";
import type { RowTone } from "./types";

export interface RowDescriptor {
  /** Lucide icon component for the gutter glyph. */
  readonly icon: LucideIcon;
  /** Tone bucket for the glyph, derived from the activity verb. */
  readonly tone: RowTone;
  /** First-line description ("Jason completed the task"). */
  readonly headline: ReactNode;
  /** Optional inline body: message text, quoted note, or a change diff. */
  readonly body?: ReactNode;
}

export function toneColor(tone: RowTone): string {
  switch (tone) {
    case "created":
    case "completed":
      return color.accent.success;
    case "removed":
      return color.accent.danger;
    case "warning":
      return color.accent.warning;
    case "info":
      return color.accent.primary;
    case "neutral":
      return color.fg.muted;
  }
}

function itemHeadline(
  actor: string,
  verb: string,
  workItem: TimelineEventWorkItem | null | undefined,
): ReactElement {
  const noun = workItem !== null && workItem !== undefined ? workItemNoun(workItem.type) : "item";
  return (
    <>
      <strong>{actor}</strong> {verb} {indefiniteArticle(noun)} {noun}
    </>
  );
}

function workItemBody(workItem: TimelineEventWorkItem | null | undefined): ReactNode {
  if (workItem === null || workItem === undefined) return null;
  if (workItem.body.length === 0) return null;
  return <WorkItemBody workItem={workItem} />;
}

/**
 * Inline before/after for an event, or `null` when the event carries no
 * change payload. Returning `null` (never `undefined`) keeps the optional
 * `RowDescriptor.body` assignment valid under `exactOptionalPropertyTypes`
 * and lets callers `??`-fallback to the work-item body.
 */
function changeBody(event: TimelineEvent): ReactNode {
  const diffs = describeChange(event);
  if (diffs.length === 0) return null;
  return <ChangeDiff diffs={diffs} />;
}

export function describeEvent(event: TimelineEvent): RowDescriptor {
  const actor = actorLabel(event);
  const item = event.workItem;

  switch (event.activityType) {
    case "workflow.created":
      return {
        icon: WorkflowIcon,
        tone: "created",
        headline: <strong>{actor}</strong>,
        body: <Text role="body">created this workflow</Text>,
      };
    case "workflow.child.spawned":
      return {
        icon: GitBranch,
        tone: "info",
        headline: (
          <>
            <strong>{actor}</strong> spawned a child workflow
          </>
        ),
      };
    case "workflow.edited":
      return {
        icon: Pencil,
        tone: "neutral",
        headline: (
          <>
            <strong>{actor}</strong> edited workflow metadata
          </>
        ),
        body: changeBody(event),
      };

    case "queue.created":
      return {
        icon: Users,
        tone: "created",
        headline: (
          <>
            <strong>{actor}</strong> created this queue
          </>
        ),
      };
    case "queue.renamed":
      return {
        icon: Pencil,
        tone: "neutral",
        headline: (
          <>
            <strong>{actor}</strong> renamed this queue
          </>
        ),
        body: changeBody(event),
      };
    case "queue.reparented":
      return {
        icon: GitBranch,
        tone: "neutral",
        headline: (
          <>
            <strong>{actor}</strong> moved this queue
          </>
        ),
        body: changeBody(event),
      };
    case "queue.member.added":
      return {
        icon: Plus,
        tone: "created",
        headline: (
          <>
            <strong>{actor}</strong> added <strong>{queueMemberLabel(event)}</strong> to this queue
          </>
        ),
      };
    case "queue.member.removed":
      return {
        icon: Trash2,
        tone: "removed",
        headline: (
          <>
            <strong>{actor}</strong> removed <strong>{queueMemberLabel(event)}</strong> from this
            queue
          </>
        ),
      };
    case "queue.deactivation.started":
      return {
        icon: AlertTriangle,
        tone: "warning",
        headline: (
          <>
            <strong>{actor}</strong> started deactivating this queue
          </>
        ),
      };
    case "queue.deactivation.blocked":
      return {
        icon: AlertTriangle,
        tone: "warning",
        headline: (
          <>
            Queue deactivation requested by <strong>{actor}</strong> was blocked
          </>
        ),
      };
    case "queue.deactivated":
      return {
        icon: Trash2,
        tone: "removed",
        headline: (
          <>
            <strong>{actor}</strong> deactivated this queue
          </>
        ),
      };
    case "queue.reactivated":
      return {
        icon: CheckCircle2,
        tone: "completed",
        headline: (
          <>
            <strong>{actor}</strong> reactivated this queue
          </>
        ),
      };
    case "queue.owner.changed":
      return {
        icon: Users,
        tone: "neutral",
        headline: (
          <>
            <strong>{actor}</strong> changed queue ownership
          </>
        ),
        body: changeBody(event),
      };
    case "queue.accessed.via-admin-role":
      return {
        icon: Eye,
        tone: "warning",
        headline: (
          <>
            <strong>{actor}</strong> administered this queue using global access
          </>
        ),
      };
    case "queue.accessed.via-management-group-admin":
      return {
        icon: Eye,
        tone: "info",
        headline: (
          <>
            <strong>{actor}</strong> administered this queue through its management group
          </>
        ),
      };
    case "managementgroup.team.pushed-to-queue":
      return {
        icon: Plus,
        tone: "created",
        headline: (
          <>
            <strong>{actor}</strong> added {queueMemberCountLabel(event, "addedUserIds")} from a
            team
          </>
        ),
      };
    case "managementgroup.team.withdrawn-from-queue":
      return {
        icon: Trash2,
        tone: "removed",
        headline: (
          <>
            <strong>{actor}</strong> removed {queueMemberCountLabel(event, "removedUserIds")} from a
            team
          </>
        ),
      };

    case "workitem.created":
      return {
        icon: Plus,
        tone: "created",
        headline: itemHeadline(actor, "added", item),
        body: workItemBody(item),
      };
    case "workitem.routed":
      return {
        icon: Route,
        tone: "info",
        headline: itemHeadline(actor, "added", item),
        body: workItemBody(item),
      };
    case "workitem.broadcasted":
      return {
        icon: Megaphone,
        tone: "info",
        headline: itemHeadline(actor, "broadcast", item),
        body: workItemBody(item),
      };
    case "workitem.addressed":
      return {
        icon: Inbox,
        tone: "info",
        headline: itemHeadline(actor, "filed", item),
        body: workItemBody(item),
      };
    case "workitem.replied":
      return {
        icon: Reply,
        tone: "info",
        headline: itemHeadline(actor, "replied", item),
        body: workItemBody(item),
      };
    case "workitem.completed":
      return {
        icon: CheckCircle2,
        tone: "completed",
        headline: itemHeadline(actor, "completed", item),
      };
    case "workitem.completed.by-reply":
      return {
        icon: CheckCircle2,
        tone: "completed",
        headline: itemHeadline(actor, "answered", item),
      };
    case "workitem.queues.changed":
      return {
        icon: Users,
        tone: "neutral",
        headline: itemHeadline(actor, "changed queue distribution on", item),
      };
    case "workitem.edited":
      return {
        icon: Edit3,
        tone: "neutral",
        headline: itemHeadline(actor, "edited", item),
        body: changeBody(event) ?? workItemBody(item),
      };
    case "note.created":
      // Notes authored before this activity type existed still arrive as
      // `workitem.routed` and render through that case.
      return {
        icon: StickyNote,
        tone: "created",
        headline: itemHeadline(actor, "added a note", item),
        body: workItemBody(item),
      };
    case "note.edited":
      return {
        icon: Edit3,
        tone: "neutral",
        headline: itemHeadline(actor, "edited a note", item),
        body: changeBody(event) ?? workItemBody(item),
      };
    case "workitem.removed":
      return {
        icon: Trash2,
        tone: "removed",
        headline: itemHeadline(actor, "removed", item),
      };
    case "workitem.approved":
      return {
        icon: ThumbsUp,
        tone: "completed",
        headline: itemHeadline(actor, "approved", item),
      };
    case "workitem.rejected":
      return {
        icon: ThumbsDown,
        tone: "warning",
        headline: itemHeadline(actor, "rejected", item),
      };
    case "workitem.snoozed":
      return {
        icon: BellOff,
        tone: "warning",
        headline: itemHeadline(actor, "snoozed", item),
        body: changeBody(event),
      };
    case "workitem.rescheduled":
      // A routine planning gesture on a not-yet-due task, so neutral tone.
      return {
        icon: CalendarClock,
        tone: "neutral",
        headline: itemHeadline(actor, "rescheduled", item),
        body: changeBody(event),
      };
    case "workitem.viewed":
      // A view has no content: actor and item only.
      return {
        icon: Eye,
        tone: "neutral",
        headline: itemHeadline(actor, "viewed", item),
      };

    default:
      // An unrecognized activity type still renders so the audit trail is
      // not censored by a UI that is behind a producer-side addition.
      return {
        icon: ChevronsRight,
        tone: "neutral",
        headline: (
          <>
            <strong>{actor}</strong> · {event.activityType}
          </>
        ),
      };
  }
}
